import { VITE_SOLANA_PUBLIC_NETWORK, VITE_SOLANA_VALIDATOR_ADDRESS } from '@/env'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { CopyIcon, SquareArrowOutUpRight } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useValidatorAPYContext } from '../providers/ValidatorAPYContext'

const EXPLORER_URL = 'https://explorer.solana.com'

const getExplorerLink = (address: string, cluster: 'devnet' | 'mainnet-beta') => {
  const base = `${EXPLORER_URL}/address/${address}`
  return cluster === 'mainnet-beta' ? base : `${base}?cluster=${cluster}`
}

const formatSol = (value: number): string => {
  if (value === 0) return '0'
  return value.toLocaleString('en-US', { maximumFractionDigits: 4, minimumFractionDigits: 0 })
}

export const ValidatorInfo = () => {
  const validatorAddress = VITE_SOLANA_VALIDATOR_ADDRESS
  const { totalStakeLamports, commission, apy, isLoading: isValidatorAPYLoading } = useValidatorAPYContext()

  const totalStakeSol = totalStakeLamports / LAMPORTS_PER_SOL

  const explorerLink = getExplorerLink(validatorAddress, VITE_SOLANA_PUBLIC_NETWORK as 'devnet' | 'mainnet-beta')

  const truncateAddress = (address: string) => {
    if (address.length <= 16) return address
    return `${address.slice(0, 6)}...${address.slice(-6)}`
  }

  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(validatorAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 500)
    } catch {}
  }, [validatorAddress])

  return (
    <div className="rounded-xl border border-gray-100 p-6">
      <div className="flex flex-col gap-6 pb-6">
        <div className="grid grid-cols-2">
          <div className="flex grow flex-col gap-2">
            <span className="text-body-3 text-gray-90">Expected APY</span>
            <span className="text-gray-70 font-semibold">{isValidatorAPYLoading ? '-' : `${apy}%`}</span>
          </div>
          <div className="flex grow flex-col gap-2 border-l border-gray-100 pl-5">
            <span className="text-body-3 text-gray-90">Commission</span>
            <span className="text-gray-70 font-semibold">{isValidatorAPYLoading ? '-' : `${commission}%`}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-gray-100 py-6">
        <span className="text-body-3 text-gray-90">Total Stake</span>
        <span className="text-gray-70 font-semibold">
          {isValidatorAPYLoading ? '-' : `${formatSol(totalStakeSol)} SOL`}
        </span>
      </div>

      <div className="flex flex-col gap-2 border-y border-gray-100 py-6">
        <span className="text-body-3 text-gray-90">Staking address</span>
        <div className="text-gray-70 flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="text-gray-70 hover:text-green flex shrink-0 gap-2 rounded p-1 transition-colors"
            title={copied ? 'Copied!' : 'Copy address'}
            aria-label={copied ? 'Copied' : 'Copy address'}
          >
            <span className="text-body-1 break-all">{truncateAddress(validatorAddress)}</span>
            {copied ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <CopyIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
      <div className="pt-6">
        <a
          href={explorerLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-70 hover:text-green flex items-center gap-2 underline transition-colors"
        >
          View on Block Explorer
          <SquareArrowOutUpRight size={18} />
        </a>
      </div>
    </div>
  )
}
